import React, { useEffect, useMemo, useState } from "react";

import { Provider } from "~/api/providers";
import * as Modal from "~/shared/components/ui/Modal/Modal";
import { useSignUpStore } from "~/stores/useSignUpStore";
import { useNow } from "../hooks/useNow";
import { formatAppointmentTime, isSlotInPast } from "../utils/dates";
import AvailabilityTime from "./ui/AvailabilityTime";
import SpecialtyTag from "./ui/SpecialtyTag";

interface ProviderDetailsModalProps {
  provider: Provider;
  isOpen: boolean;
  onClose: () => void;
  onTimeSelect?: (providerId: string, time: string) => void;
}

const ProviderDetailsModal: React.FC<ProviderDetailsModalProps> = ({
  provider,
  isOpen,
  onClose,
}) => {
  const firstName = provider.fullName.split(" ")[0];
  const { patient } = useSignUpStore();
  const [showAllSpecialties, setShowAllSpecialties] = useState(false);

  // Same cutoff the card applies, so the details view cannot advertise a time
  // the card has already stopped offering.
  const now = useNow();
  const bookableSlots = useMemo(
    () => provider.availableSlots.filter((slot) => !isSlotInPast(slot, now)),
    [provider.availableSlots, now],
  );

  useEffect(() => {
    if (!isOpen) {
      setShowAllSpecialties(false);
    }
  }, [isOpen]);

  const specialties = provider.metadata?.specialties || [];
  const userConditions = patient.conditions || [];

  const matchingSpecialties = specialties.filter((specialty: string) =>
    userConditions.some((condition: string) =>
      specialty.toLowerCase().includes(condition.toLowerCase()),
    ),
  );

  const nonMatchingSpecialties = specialties.filter(
    (specialty: string) =>
      !userConditions.some((condition: string) =>
        specialty.toLowerCase().includes(condition.toLowerCase()),
      ),
  );

  const allSpecialties = [...matchingSpecialties, ...nonMatchingSpecialties];
  const displayedSpecialties = showAllSpecialties
    ? allSpecialties
    : allSpecialties.slice(0, 5);
  const remainingCount = Math.max(0, allSpecialties.length - 5);

  //TODO: Add provider credentials
  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content className="flex max-h-[90vh] w-full max-w-2xl flex-col bg-white p-6 text-black">
        <Modal.CloseButton onClick={onClose} />

        <Modal.Header className="mb-6">
          <div className="flex items-center gap-4">
            {provider.avatarUrl ? (
              <img
                src={provider.avatarUrl}
                alt={`${provider.fullName} profile`}
                className="my-auto aspect-square w-16 shrink-0 self-stretch rounded-[50px] object-cover"
              />
            ) : (
              <div className="my-auto flex aspect-square w-16 shrink-0 items-center justify-center self-stretch rounded-[50px] bg-gray-200 text-gray-500">
                <span className="font-display text-2xl">
                  {provider.fullName ? provider.fullName.charAt(0) : "P"}
                </span>
              </div>
            )}
            <div>
              <Modal.Title>{provider.fullName}</Modal.Title>
              <Modal.Description className="mt-1 text-neutral-500">
                {provider.credentials}
              </Modal.Description>
            </div>
          </div>
        </Modal.Header>

        <div className="space-y-6 overflow-y-auto pr-2">
          <section className="w-full">
            <h3 className="text-gray-450 mb-3 text-sm font-semibold">
              SPECIALTIES
            </h3>
            <div className="text-green-650 flex w-full flex-wrap items-start gap-2">
              {displayedSpecialties.map((specialty, index) => (
                <SpecialtyTag
                  key={`${provider.id}-specialty-${index}`}
                  name={specialty}
                />
              ))}
              {remainingCount > 0 && !showAllSpecialties && (
                <button
                  onClick={() => setShowAllSpecialties(true)}
                  className="bg-green-150 flex cursor-pointer flex-col justify-center rounded-lg border-2 border-green-300 px-1.5 py-1"
                >
                  <div className="flex items-center gap-1">
                    <span className="mx-1.5 my-auto cursor-pointer self-stretch">
                      +{remainingCount} more
                    </span>
                  </div>
                </button>
              )}
            </div>
          </section>

          <section className="w-full">
            <h3 className="text-gray-450 mb-3 text-sm font-semibold">
              AVAILABILITY
            </h3>
            <div className="text-brown-650 mt-3 grid grid-cols-4 items-start gap-3 self-start text-center text-base leading-none font-medium tracking-normal">
              {bookableSlots.map((slot, index) => {
                return (
                  <AvailabilityTime
                    key={`${provider.id}-time-${index}`}
                    time={formatAppointmentTime(slot)}
                    selected={false}
                    isButton={false}
                  />
                );
              })}
            </div>
          </section>
          <section className="w-full">
            <h3 className="text-gray-450 mb-3 text-sm font-semibold">
              ABOUT {firstName.toUpperCase()}
            </h3>
            <div className="text-sm text-black">
              {provider.metadata?.biography
                ?.split("\n\n")
                .map((paragraph, index) => (
                  <p
                    key={`${provider.id}-bio-${index}`}
                    className={index > 0 ? "mt-3" : ""}
                  >
                    {paragraph}
                  </p>
                ))}
            </div>
          </section>

          <section className="w-full">
            <h3 className="text-gray-450 mb-3 text-sm font-semibold">
              SEEING CLIENTS IN:
            </h3>
            <p className="text-base text-black">
              {provider.licensedIn?.join(", ")}
            </p>
          </section>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};

export default ProviderDetailsModal;
